import { LeadImport } from "model/lead-import";
import { validate, isEmail } from "shared/validate";
import moment from "moment";
import { LeadTagType } from "type/lead-tag";
import { LeadPipelineType } from "type/lead-pipeline";
import { LeadStatus } from "type/lead-status";

export interface TableData extends LeadImport {
  validationErrors?: {
    email?: string;
    birthday?: string;
    contactType?: string;
    transactionAnniversary?: string;
    piplineType?: string;
  };
  validationWarnings?: {
    email?: string;
    agentId?: string;
    marketIds?: string;
    campaignIds?: string;
  };
}

export function validatedTableData(
  markets: any[],
  leads: any[],
  campaigns: any[],
  agents: any[],
  leadImports: LeadImport[],
  shouldValidate?: boolean
): TableData[] {
  return leadImports.map((leadImport) => {
    const validationWarnings = validate({
      email: () => {
        if (shouldValidate) {
          const isDuplicate =
            leadImports.filter((result) => leadImport.email === result.email).length > 1;
          if (isEmail(leadImport.email) && isDuplicate) {
            return "Email appears in this data multiple times. Only the first record will be imported.";
          }
        }
      },
      agentId: () => {
        if (shouldValidate && leadImport.agentId) {
          if (!agents.some((agent) => agent.id === leadImport.agentId)) {
            return `The lead will be imported but not assigned to ${leadImport.agentId}. The AgentID does not exist.`;
          }
        }
      },
      marketIds: () => {
        if (shouldValidate && leadImport.marketIds) {
          const ids = leadImport.marketIds
            .map((id) => {
              if (!markets.some((market) => market.id === id)) {
                return id.toString();
              }
              return null;
            })
            .filter(Boolean) as string[];
          if (ids.length > 0) {
            return `The lead can be imported without subscribing to markets. Could not find MarkedIDs for: ${ids.join(", ")}.`;
          }
        }
      },
      campaignIds: () => {
        if (shouldValidate && leadImport.campaignIds) {
          const ids = leadImport.campaignIds
            .map((id) => {
              if (!campaigns.some((campaign) => campaign.id === id)) {
                return id.toString();
              }
              return null;
            })
            .filter(Boolean) as string[];
          if (ids.length > 0) {
            return `The lead can be imported without subscribing to campaigns. Could not find CampaignIDs for: ${ids.join(", ")}.`;
          }
        }
      },
    });
    const validationErrors = validate({
      email: () => {
        if (shouldValidate) {
          if (!leadImport.email) {
            return "Email is Required.";
          }
          if (!isEmail(leadImport.email)) {
            return "Email is not formatted correctly.";
          }
          if (
            leads.some((lead) => {
              if (lead.status === LeadStatus.ACTIVE) {
                return lead.email === leadImport.email;
              }
              return false;
            })
          ) {
            return `Email is already in use.`;
          }
        }
      },
      birthday: () => {
        if (shouldValidate && leadImport.birthday) {
          if (!moment(leadImport.birthday, "MM/DD/YYYY", true).isValid()) {
            return `Invalid Date: Use MM/DD/YYYY.`;
          }
        }
      },
      contactType: () => {
        if (shouldValidate && leadImport.contactType) {
          if (!LeadTagType.getLeadTypeById(leadImport.contactType)) {
            return `No such contact type. Supported options are:${LeadTagType.BUYER.label}, ${LeadTagType.SELLER.label} or ${LeadTagType.RENTER.label} are suported`;
          }
        }
      },
      piplineType: () => {
        if (shouldValidate && leadImport.pipelineType) {
          const typeLabels = LeadPipelineType.values().map((value) => value.label);
          if (!LeadPipelineType.getByLabel(leadImport.pipelineType)) {
            return `No such pipeline. Supported options are: ${typeLabels.join(", ")}.`;
          }
        }
      },
      transactionAnniversary: () => {
        if (shouldValidate && leadImport.transactionAnniversary) {
          if (!moment(leadImport.transactionAnniversary, "MM/DD/YYYY", true).isValid()) {
            return `Invalid Date: Use MM/DD/YYYY.`;
          }
        }
      },
    });
    return {
      ...leadImport,
      validationWarnings,
      validationErrors,
    };
  });
}
